import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { awards as awardsService } from "../../api/awards.service.js";
import type { Award, AwardBulkChanges } from "../../api/awards.types.js";
import { DataFilters } from "../../components/DataFilters/DataFilters.js";
import { DataTable } from "../../components/DataTable/DataTable.js";
import EmptyState from "../../components/EmptyState/index.js";
import { ErrorBanner } from "../../components/ErrorBanner/ErrorBanner.js";
import LoadingState from "../../components/LoadingState/index.js";
import { PageHeader } from "../../components/PageHeader/PageHeader.js";
import { PaginationControls } from "../../components/PaginationControls/index.js";
import { AddressBookContactPicker } from "../../components/Profiles/ListFilters/AddressBookContactPicker.js";
import { CompanyPicker } from "../../components/Profiles/ListFilters/CompanyPicker.js";
import { useCompanyNames } from "../../components/Profiles/ListFilters/useCompanyNames.js";
import { UnauthorizedView } from "../../components/UnauthorizedView/index.js";
import { AWARDS_WRITE_PERMISSION } from "../../constants/permissions.js";
import { useAwardFilterOptions } from "../../hooks/useAwardFilterOptions.js";
import { useAwards } from "../../hooks/useAwards.js";
import { useFilters } from "../../hooks/useFilters.js";
import { usePermissions } from "../../hooks/usePermissions.js";
import { useUrlParams } from "../../hooks/useUrlParams.js";
import { ROUTES } from "../../routes.js";
import { downloadBlob } from "../../utils/csvExport.js";
import { createAwardColumns } from "./columns.js";
import { BulkEditModal } from "./components/BulkEditModal.js";
import { LinkFirmModal } from "./components/LinkFirmModal.js";
import { LinkLawyerModal } from "./components/LinkLawyerModal.js";
import {
  ALL_FILTER_VALUE,
  AWARD_FILTER_LABELS,
  buildAwardFilters,
  clearedAwardFilters,
  getAwardFilterOptionLabel,
  readAwardFilters,
  toAwardShortlistTags,
  toAwardStates,
  toAwardStatuses,
  toCountryIds,
  toIncompleteFilter,
  toSlugFilter,
  toUuid,
  toYears,
  type AwardFilterValues,
} from "./filters.js";
import { useContactName } from "./useContactName.js";
import { useCountryNames } from "./useCountryNames.js";
import styles from "./Awards.module.css";

/**
 * Server-side page size. Fixed for now: the list has no rows-per-page control,
 * so the only thing that moves the window is the page number.
 */
const PAGE_SIZE = 10;

/**
 * Reads the page number out of the URL. The URL is the single source of truth —
 * no mirrored `useState` — so Back/Forward and a pasted `?page=3` all take the
 * same path, and the fetch below sees one primitive change per navigation.
 *
 * Everything that is not a whole page above 1 collapses to 1: `?page=abc` (NaN),
 * `?page=0` and `?page=-3` would otherwise send a negative `offset` upstream.
 */
function readCurrentPage(searchParams: URLSearchParams): number {
  const parsed = Number(searchParams.get("page"));
  return Number.isFinite(parsed) && parsed > 1 ? Math.floor(parsed) : 1;
}

/**
 * `useFilters` writes each change to two places: `updateUrl`, and a `setFilters`
 * mirror it expects the caller to hold. This page holds no mirror — the URL *is*
 * the filter state, and the next render reads it straight back out of
 * `searchParams` — so the setter its signature requires does nothing. Hoisted to
 * module scope because a no-op is the same no-op on every render.
 */
const IGNORE_FILTER_STATE: React.Dispatch<React.SetStateAction<AwardFilterValues>> = () =>
  undefined;

/**
 * Awards list.
 *
 * Reaching the page at all is gated by the `RequirePermission` wrapper on the
 * route in App.tsx, so nothing below re-checks the *read* permission — the
 * 401/403 rung is about the API disagreeing with the token, not about the route.
 * The write permission is a separate question and is checked here, because it
 * decides what a user who is already on the page may do rather than whether they
 * belong on it.
 *
 * The state ladder deliberately sits *in front of* the table: `DataTable`
 * renders its own bare `.emptyText` on an empty array, so the empty rung has to
 * short-circuit before it ever gets there.
 */
export default function AwardsPage() {
  const navigate = useNavigate();
  const { searchParams, updateUrl } = useUrlParams();
  const currentPage = readCurrentPage(searchParams);

  // The URL is the filter state, the same way it is already the page number —
  // one object read per render, feeding all three things that have to agree: the
  // request below, the dropdowns (`DataFilters` is controlled) and the chips.
  //
  // What this replaces is a `useState` seeded from the URL once, which left the
  // page with two sources of truth: any URL change that did not come from a
  // dropdown — Back and Forward above all — reloaded the table against the new
  // URL while the dropdowns and chips still showed the old selection, so the
  // page claimed "Status: Winner" over unfiltered rows.
  //
  // The mirror cannot come back for the request either: react-router commits a
  // navigation inside a transition, so a `setState` lands one render before the
  // new URL, and a request built from both sources fires twice per change.
  //
  // `useMemo` keeps one object identity per URL — react-router memoises
  // `searchParams` per `location.search` — which is what the `activeFilters`
  // memo inside `useFilters` and `enhancedActiveFilters` below are keyed on.
  const filters = useMemo(() => readAwardFilters(searchParams), [searchParams]);

  // The chip needs the firm's name and the URL only carries its uuid.
  // `useCompanyNames` is the same module-cached resolver the Profiles list uses
  // for its own firm chips, and it issues nothing while no firm is selected.
  const firmId = toUuid(filters.firm);
  const firmIds = useMemo(() => (firmId ? [firmId] : []), [firmId]);
  const companyNames = useCompanyNames(firmIds);
  const selectedFirm = useMemo(() => {
    const name = firmId ? companyNames[firmId] : undefined;
    return firmId && name ? { id: firmId, name } : null;
  }, [firmId, companyNames]);

  // Same for the lawyer: the URL carries a contact uuid, the chip wants the
  // person's name. `useContactName` resolves it through the Address Book and,
  // like the firm's resolver, issues nothing while no lawyer is selected.
  const lawyerId = toUuid(filters.lawyer);
  const lawyerName = useContactName(lawyerId);
  const selectedLawyer = useMemo(
    () => (lawyerId && lawyerName ? { id: lawyerId, name: lawyerName } : null),
    [lawyerId, lawyerName],
  );

  // One subscription for the whole page: the region-countries tree resolves the
  // raw `jurisdictionCountryIds` the list returns (§3.5) for the table's
  // Jurisdictions chips below, and now also supplies the Jurisdiction(s)
  // dropdown's option labels in `filterConfigs`. Its `loading`/`error` stay out
  // of the list's state ladder below on purpose — the tree is a second,
  // independent request, and a slow or broken taxonomy must degrade to the
  // `Country ${id}` fallback everywhere it is used, never hold up or fail the
  // awards list.
  const { getCountryLabel } = useCountryNames();

  // The dictionary dropdowns (Product / Type of Award / Award Series / Year /
  // Jurisdiction(s)) are the only consumer of this request; its `loading`/`error`
  // deliberately stay out of the list's state ladder below, the same way
  // `useCountryNames` does — a slow or failed `filter-options` call disables
  // those five dropdowns and never holds up or fails the list itself.
  const { data: filterOptions, loading: filterOptionsLoading } = useAwardFilterOptions();
  const filterConfigs = useMemo(
    () =>
      buildAwardFilters({
        options: filterOptions,
        loading: filterOptionsLoading,
        getCountryLabel,
        selectedFirm,
        selectedLawyer,
      }),
    [filterOptions, filterOptionsLoading, getCountryLabel, selectedFirm, selectedLawyer],
  );

  const { activeFilters, handleFilterChange, handleClearFilter } = useFilters({
    filters,
    setFilters: IGNORE_FILTER_STATE,
    updateUrl,
    labelOverrides: AWARD_FILTER_LABELS,
  });

  // Ids, not rows, and across pages: the selection feeds the export, and a
  // user narrowing the list to pick rows on page 1 and then page 3 is the whole
  // reason it is a Set rather than "the checked rows in the table". A filter
  // change empties it (below), because the rows it names may no longer be in the
  // result set the user is looking at, and "Export selected (4)" over a table
  // showing none of the four would be a lie.
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());

  // A failed bulk write's error and change set live next to the selection
  // because they are only meaningful against it: the banner's retry resends
  // the change set to the rows that are selected *now*. Once the user clears
  // or refilters the selection, those rows are gone, and a retry would either
  // send no ids or apply the old change set to whatever gets selected next.
  // So clearing the selection discards the pending retry with it.
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [lastBulkChanges, setLastBulkChanges] = useState<AwardBulkChanges | null>(null);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setBulkError(null);
    setLastBulkChanges(null);
  }, []);

  // The single path every filter change takes, because three things have to
  // move together. A filter narrows the result set, so whatever page 3 addressed
  // before no longer exists: `page: ""` drops the param (page 1 is spelled as its
  // absence), `currentPage` falls back to 1, and that is what puts `offset=0` on
  // the same request that carries the new filter. Both go into one `updateUrl`
  // call, so they reach the URL together rather than a render apart. The
  // selection goes with them, for the reason given above it.
  const handleFiltersChange = (key: string, value: string | number | boolean) => {
    clearSelection();
    handleFilterChange(key, value, { page: "" });
  };

  const handleFiltersClear = (key: string) => {
    clearSelection();
    handleClearFilter(key, { page: "" });
  };

  // Bypasses `useFilters` entirely: `handleFilterChange` writes one `updateUrl`
  // call per key, and each call is its own navigation and its own refetch, so
  // clearing ten keys through it would fire ten requests. One `updateUrl` call
  // with every key at its sentinel is one navigation and one refetch instead.
  const handleFiltersClearAll = () => {
    clearSelection();
    updateUrl(clearedAwardFilters());
  };

  // Everything the request is made of comes from the URL — the page number and
  // the filters — so one navigation changes it exactly once, whether that
  // navigation came from a dropdown, the pager or the browser's Back button.
  const { data, loading, error, refetch, isRefetching } = useAwards({
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    // `undefined` for the `"all"` sentinel, an empty selection and any unknown
    // values the URL might be carrying — the service then drops the parameter
    // entirely rather than sending one it would have to special-case. Otherwise,
    // a multi-select's CSV becomes the array `awards.service.ts` serialises
    // straight back into one CSV param. Same rule for the dictionary slugs, the
    // year, the jurisdiction, the firm and the lawyer: `toSlugFilter`/`toYears`/
    // `toCountryIds`/`toUuid` apply it on the way out, independently of whether
    // `awards/filter-options` has resolved — and of whether the firm's or the
    // lawyer's name has.
    product: toSlugFilter(filters.product),
    awardType: toSlugFilter(filters.type),
    series: toSlugFilter(filters.series),
    companyId: firmId,
    contactId: lawyerId,
    status: toAwardStatuses(filters.status),
    shortlistTag: toAwardShortlistTags(filters.shortlistTag),
    state: toAwardStates(filters.publishStatus),
    year: toYears(filters.year),
    countryId: toCountryIds(filters.jurisdiction),
    incomplete: toIncompleteFilter(filters.linking),
  });

  const awardRows = data?.awards ?? [];

  // The envelope the server echoes back, not what the page asked for: `total`
  // and the window it actually served are what the counter and the controls
  // below are allowed to claim.
  const pagination = data?.pagination ?? {
    total: 0,
    limit: PAGE_SIZE,
    offset: 0,
    hasMore: false,
  };
  const totalPages = Math.max(1, Math.ceil(pagination.total / PAGE_SIZE));
  // Shared by the counter and PaginationControls below, so a filtered-to-one
  // result cannot read "1 awards" in one place and "1 award" in the other.
  const awardsNoun = pagination.total === 1 ? "award" : "awards";

  const goToPage = (page: number) => {
    // Page 1 is the default, so it is spelled as the *absence* of the param —
    // `updateUrl` drops any key whose value is the empty string, which keeps
    // `/awards` and `/awards?page=1` from being two URLs for one view.
    updateUrl({ page: page > 1 ? String(page) : "" });
  };

  // The route guard only checks `staff.awards.read`, and the two permissions are
  // genuinely different audiences: `staff.product` holds the read and not the
  // write (packages/permissions roles.json). That role sees the "needs linking"
  // marks — knowing which rows are incomplete is half of why the column shows
  // them — and gets no button to press.
  const { hasPermission } = usePermissions();
  // One permission for everything that writes: both halves of the match repair
  // (`PATCH /v1/awards/:id/match` takes a partial body, so linking a firm and
  // linking a lawyer are the same write), and creating or editing a row.
  const canWriteAwards = hasPermission(AWARDS_WRITE_PERMISSION);

  // The modals belong to the page, not to the cell: the fix changes which rows
  // the server would return for the current filter and page, so whatever reacts
  // to it has to be the thing that owns the request.
  //
  // Two pieces of state rather than one row plus a "which modal" flag, because
  // the two modals take different props and neither is a mode of the other. They
  // are kept mutually exclusive by the openers below: the marks are two separate
  // buttons in one cell, and a stray second click must not stack a dialog on top
  // of a dialog.
  const [awardToLinkFirm, setAwardToLinkFirm] = useState<Award | null>(null);
  const [awardToLinkLawyer, setAwardToLinkLawyer] = useState<Award | null>(null);

  // `useCallback`, because `columns` is memoised: a fresh closure per render
  // would rebuild every column on every render and throw away the memo.
  const handleLinkFirm = useCallback((award: Award) => {
    setAwardToLinkLawyer(null);
    setAwardToLinkFirm(award);
  }, []);

  const handleLinkLawyer = useCallback((award: Award) => {
    setAwardToLinkFirm(null);
    setAwardToLinkLawyer(award);
  }, []);

  // One handler for both, because the consequence is identical: close whatever
  // was open and let the server say what the row now reads as.
  const handleLinked = useCallback(() => {
    setAwardToLinkFirm(null);
    setAwardToLinkLawyer(null);
    // Refetch rather than patch the row in place. The list is server-filtered
    // and server-paged: a row that was just fixed may no longer belong on the
    // page being shown, and the resolved name is the server's to supply.
    refetch();
  }, [refetch]);

  const handleEdit = useCallback(
    (award: Award) => {
      navigate(ROUTES.awards.edit(award.id));
    },
    [navigate],
  );

  const handleToggleAward = useCallback((id: string) => {
    setSelectedIds(current => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // The header checkbox speaks for the page it is on, not for the whole
  // selection: checked means every visible row is in the set, and one click
  // moves all of them the same way — out if they are all in, otherwise in.
  // Selections made on other pages are left exactly as they were.
  const allOnPageSelected = awardRows.length > 0 && awardRows.every(row => selectedIds.has(row.id));

  const handleTogglePage = useCallback(() => {
    setSelectedIds(current => {
      const next = new Set(current);
      for (const row of awardRows) {
        if (allOnPageSelected) {
          next.delete(row.id);
        } else {
          next.add(row.id);
        }
      }
      return next;
    });
  }, [awardRows, allOnPageSelected]);

  // Not gated on `staff.awards.write`: the export is another way of reading the
  // list, and the same read permission that let the user onto the page is what
  // the endpoint checks. Its own error state rather than the list's — a failed
  // download must leave the table the user was selecting from where it is.
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      const { blob, filename } = await awardsService.export(Array.from(selectedIds));
      downloadBlob(blob, filename);
    } catch (exportFailure) {
      setExportError(
        exportFailure instanceof Error ? exportFailure.message : "Failed to export awards",
      );
    } finally {
      setIsExporting(false);
    }
  };

  const exportLabel = selectedIds.size > 0 ? `Export selected (${selectedIds.size})` : "Export";

  // The bulk write is the page's, whichever button asked for it: Mark
  // Published, Mark Draft and the modal's Apply all end in the same request, the
  // same refetch and the same emptied selection. Its own error state, as the
  // export has (declared beside the selection above), so a failed write leaves
  // the table where it is. The change set is kept so the banner's retry can
  // send it again against the selection — which survives a failure on purpose,
  // so the retry has something to address.
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const applyBulkChanges = async (changes: AwardBulkChanges) => {
    setIsBulkUpdating(true);
    setBulkError(null);
    setLastBulkChanges(changes);
    try {
      const { updatedCount } = await awardsService.bulkUpdate(Array.from(selectedIds), changes);
      setIsBulkEditOpen(false);
      clearSelection();
      // The server's count, not the selection's: it reports rows actually
      // written, and skips ids that no longer exist.
      setToast({
        message: `${updatedCount} ${updatedCount === 1 ? "award" : "awards"} updated`,
        type: "success",
      });
      refetch();
    } catch (bulkFailure) {
      // The modal closes so the banner underneath is visible; its retry
      // resends the same change set, so nothing the user ticked is lost.
      setIsBulkEditOpen(false);
      setBulkError(bulkFailure instanceof Error ? bulkFailure.message : "Failed to update awards");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const retryBulkChanges = () => {
    if (lastBulkChanges) applyBulkChanges(lastBulkChanges);
  };

  const columns = useMemo(
    () =>
      createAwardColumns({
        selectedIds,
        allOnPageSelected,
        onToggleAward: handleToggleAward,
        onTogglePage: handleTogglePage,
        getCountryLabel,
        onLinkFirm: canWriteAwards ? handleLinkFirm : undefined,
        // Permission only. Whether *this* row can offer it — it needs a linked
        // firm to scope the contact search to — is the cell's call, because the
        // cell is what holds the row's `companyId`.
        onLinkLawyer: canWriteAwards ? handleLinkLawyer : undefined,
        onEdit: canWriteAwards ? handleEdit : undefined,
      }),
    [
      selectedIds,
      allOnPageSelected,
      handleToggleAward,
      handleTogglePage,
      getCountryLabel,
      canWriteAwards,
      handleLinkFirm,
      handleLinkLawyer,
      handleEdit,
    ],
  );

  // `useFilters` puts the *wire* value on the chip, so an untouched chip would
  // read "Status: highly_commended". `displayValue` is the hook `DataFilters`
  // gives for that (same shape as SubmissionsList and Testimonials), and the
  // label comes from the dropdown config, so the chip and the dropdown can never
  // disagree about what a value is called.
  const enhancedActiveFilters = useMemo(
    () =>
      activeFilters.map(filter => ({
        ...filter,
        displayValue: getAwardFilterOptionLabel(filter.key, filter.value, filterConfigs),
      })),
    [activeFilters, filterConfigs],
  );

  // api/client.ts hangs the HTTP status on the Error it throws; that status is the
  // only thing separating "you may not see this" from "the request failed".
  const isAuthError =
    error &&
    ((error as Error & { status?: number }).status === 401 ||
      (error as Error & { status?: number }).status === 403);

  // The counter now rides in the filter row, which — unlike the ladder below —
  // renders on every rung, because the filters have to stay reachable when the
  // result they produced is empty or the request failed. So it repeats the
  // ladder's last condition instead of inheriting it: under loading, an error, a
  // 403 or an empty result there is no window to describe, and a stale
  // "25 awards" next to "Failed to load awards" is worse than no counter at all.
  const hasResolvedRows = !loading && !isRefetching && !error && awardRows.length > 0;

  const renderListContent = () => {
    // `isRefetching` as well as `loading`: useQuery only raises `loading` when the
    // deps change, so a Retry would otherwise fall through to the empty rung and
    // flash "No awards found" until the response lands.
    if (loading || isRefetching) {
      return <LoadingState message="Loading awards..." />;
    }

    if (isAuthError) {
      return <UnauthorizedView />;
    }

    if (error) {
      return (
        <EmptyState
          variant="error"
          title="Failed to load awards"
          message={error.message}
          action={{
            label: "Retry",
            onClick: () => refetch(),
          }}
        />
      );
    }

    if (awardRows.length === 0) {
      return (
        <EmptyState
          variant="empty"
          title="No awards found"
          message="Awards will appear here once they have been imported."
        />
      );
    }

    // The controls belong to this rung only: under loading, an error, a 403 or an
    // empty result there is no window to describe, and a stale
    // "Showing 1-10 of 25" next to an error message is worse than none.
    //
    // `emptyMessage` on DataTable is left at its default: the empty rung above
    // has already short-circuited, so it never reaches its own empty branch.
    return (
      <>
        <DataTable data={awardRows} columns={columns} />
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          // Both indices are 0-based, because PaginationControls renders
          // `startIndex + 1` itself. Feeding it the 1-based start that some
          // pages compute would print "Showing 2-25 of 25".
          startIndex={pagination.offset}
          endIndex={pagination.offset + awardRows.length}
          totalCount={pagination.total}
          itemName={awardsNoun}
          onPageChange={goToPage}
        />
      </>
    );
  };

  // The pickers are JSX bound to this render's `handleFiltersChange`, so they
  // are attached after the memo rather than built inside it: an element in the
  // dependency list would recompute the configs on every render and make the
  // memo a lie. The chips keep reading `filterConfigs`, which carries the
  // firm's and the lawyer's names for them; only `DataFilters` needs the
  // controls.
  const firmPicker = (
    <CompanyPicker
      value={firmId ?? null}
      onChange={companyId => handleFiltersChange("firm", companyId ?? ALL_FILTER_VALUE)}
      label="Firm"
      placeholder="All firms"
    />
  );
  // The lawyer does not depend on the firm: with none selected the picker
  // searches the whole Address Book, and a selected firm only narrows the
  // search to that firm's roster. Neither filter clears the other — a lawyer
  // from one firm combined with another firm is an honest empty list, because
  // the API ANDs the two.
  const lawyerPicker = (
    <AddressBookContactPicker
      companyId={firmId ?? null}
      companyOptional
      value={lawyerId ?? null}
      onChange={contactId => handleFiltersChange("lawyer", contactId ?? ALL_FILTER_VALUE)}
      label="Associated Lawyer"
      placeholder="All lawyers"
    />
  );
  const filterConfigsWithControls = filterConfigs.map(config => {
    if (config.key === "firm") return { ...config, control: firmPicker };
    if (config.key === "lawyer") return { ...config, control: lawyerPicker };
    return config;
  });

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Awards">
        <div className={styles.headerActions}>
          <Button variant="outlined" color="default" onClick={handleExport} disabled={isExporting}>
            {isExporting ? "Exporting..." : exportLabel}
          </Button>
          {canWriteAwards && (
            <Button variant="contained" color="primary" onClick={() => navigate(ROUTES.awards.new)}>
              New Award
            </Button>
          )}
        </div>
      </PageHeader>
      {/* Outside the ladder on purpose: a filter that returns nothing has to be
          changeable, which it is not if the whole row disappears with the table.
          `onClearAll` bypasses `useFilters` on purpose (see `handleFiltersClearAll`
          above) — one navigation instead of one per key. */}
      <div className={styles.filters}>
        <DataFilters
          filters={filterConfigsWithControls}
          values={filters}
          onChange={handleFiltersChange}
          activeFilters={enhancedActiveFilters}
          onClearFilter={handleFiltersClear}
          onClearAll={handleFiltersClearAll}
          // Eleven controls: the mock's tidy equal-cell grid, not the flex row's fill-the-line growth.
          layout="grid"
        >
          {hasResolvedRows && (
            <span className={styles.resultCount}>
              {pagination.total} {awardsNoun}
            </span>
          )}
        </DataFilters>
      </div>
      {exportError && (
        <ErrorBanner error={exportError} onRetry={handleExport} loading={isExporting} />
      )}
      {bulkError && (
        <ErrorBanner error={bulkError} onRetry={retryBulkChanges} loading={isBulkUpdating} />
      )}
      {/* Outside the ladder as well: the selection survives a page change, and
          the bar has to keep saying so while the next page loads. */}
      {selectedIds.size > 0 && (
        <div className={styles.selectionBar}>
          <span className={styles.selectedCount}>{selectedIds.size} selected</span>
          <Button variant="text" color="default" size="small" onClick={clearSelection}>
            Clear
          </Button>
          {/* The same permission as the row-level writes: the read-only role
              keeps the selection for the export and gets no way to change rows. */}
          {canWriteAwards && (
            <>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => applyBulkChanges({ state: "published" })}
                disabled={isBulkUpdating}
              >
                Mark Published
              </Button>
              <Button
                variant="outlined"
                color="default"
                size="small"
                onClick={() => applyBulkChanges({ state: "draft" })}
                disabled={isBulkUpdating}
              >
                Mark Draft
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setIsBulkEditOpen(true)}
                disabled={isBulkUpdating}
              >
                Bulk Edit…
              </Button>
            </>
          )}
        </div>
      )}
      {renderListContent()}
      {/* Siblings of the list, not children of the cell that opened them: a
          failed link has to leave the modal standing with its error, and the
          refetch a successful one triggers unmounts the table for a beat.
          Anything rendered inside the row would go with it. */}
      {awardToLinkFirm && (
        <LinkFirmModal
          awardId={awardToLinkFirm.id}
          firmNameRaw={awardToLinkFirm.firmNameRaw}
          onClose={() => setAwardToLinkFirm(null)}
          onLinked={handleLinked}
        />
      )}
      {/* The `companyId !== null` test is a type-level restatement of something
          the cell already guarantees — it offers no lawyer button on a row
          without a firm — but the modal's `companyId` prop is non-nullable and
          `Award["companyId"]` is not, so this is where the two meet. */}
      {isBulkEditOpen && (
        <BulkEditModal
          selectedCount={selectedIds.size}
          submitting={isBulkUpdating}
          onApply={applyBulkChanges}
          onClose={() => setIsBulkEditOpen(false)}
        />
      )}
      {awardToLinkLawyer && awardToLinkLawyer.companyId !== null && (
        <LinkLawyerModal
          awardId={awardToLinkLawyer.id}
          lawyerNameRaw={awardToLinkLawyer.lawyerNameRaw}
          companyId={awardToLinkLawyer.companyId}
          firmName={awardToLinkLawyer.firmName}
          onClose={() => setAwardToLinkLawyer(null)}
          onLinked={handleLinked}
        />
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}
